/** Who's on the planet right now, and who else is in this world, with how much each changed. */
import type { JSX } from 'react';

import { t } from '@i18n/index';
import { Face } from '@ui/components/Sheet';
import { useGameState, useLocale } from '@ui/hooks';

export function People(): JSX.Element | null {
  useLocale();
  const me = useGameState((s) => s.me);
  const members = useGameState((s) => s.members);
  const online = useGameState((s) => s.online);
  const view = useGameState((s) => s.view);
  if (!me) return null;
  const actionsOf = (id: string): number => view?.players.get(id)?.actions ?? 0;
  const nameOf = (id: string): string =>
    id === me.id ? t('online.you') : (members.get(id)?.name ?? t('hud.crewmate'));
  const colorOf = (id: string): string =>
    id === me.id ? me.color : (members.get(id)?.color ?? '#999999');
  const here = [me.id, ...online.keys()];
  const others = [...members.keys()]
    .filter((id) => !here.includes(id))
    .sort((a, b) => actionsOf(b) - actionsOf(a));
  const row = (id: string, on: boolean): JSX.Element => (
    <li key={id}>
      <Face color={colorOf(id)} />
      <span className="who">
        <b>{nameOf(id)}</b>
        <small>{t('online.changes', { n: actionsOf(id) })}</small>
      </span>
      {on && <span className="on">{t('online.here')}</span>}
    </li>
  );
  return (
    <section className="people" aria-labelledby="people-title">
      <h3 id="people-title">{t('online.title')}</h3>
      <ul className="list">{here.map((id) => row(id, true))}</ul>
      {online.size === 0 && <p className="note">{t('online.nobody')}</p>}
      {others.length > 0 && (
        <>
          <h4>{t('online.others')}</h4>
          <ul className="list">{others.map((id) => row(id, false))}</ul>
        </>
      )}
    </section>
  );
}
